// The gate's visual states (spec §8.6).
//
// Framework-light on purpose (tech spec §10): a service worker, IndexedDB and
// a camera loop already interact in enough ways without SSR or hydration in
// the mix.
import { VERDICT } from './verify.js';

const app = () => document.getElementById('app');

export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else if (k.startsWith('on')) node.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v !== null && v !== undefined) node.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export function render(...nodes) {
  const root = app();
  root.replaceChildren(...nodes);
}

/**
 * The status bar (FR-030): connectivity, freshness, and how many verdicts are
 * waiting to be sent.
 *
 * "Last synced" is shown as a timestamp rather than dressed up as live. Offline
 * numbers are only ever as fresh as the last download, and pretending otherwise
 * is how a stale device gets trusted.
 */
export function statusBar({ gateName, label, online, lastSyncedAt, ticketCount, queued, onSync }) {
  return el('div', { class: 'bar barTop' }, [
    el('div', {}, [
      el('div', { class: 'barGate' }, `${gateName ?? 'Gate'} — ${label ?? 'device'}`),
      el('div', { class: 'barMeta' }, [
        el('span', { class: `dot ${online ? 'dotOnline' : 'dotOffline'}` }),
        `${online ? 'online' : 'offline'} · ${ticketCount ?? 0} tickets · synced ${
          lastSyncedAt ? new Date(lastSyncedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'never'
        } · ${queued ?? 0} queued`,
      ]),
    ]),
    el('button', { class: 'btn', onclick: onSync }, 'Sync now'),
  ]);
}

const FACE = {
  [VERDICT.ADMIT]: { glyph: '✓', word: 'Admit', note: null },
  [VERDICT.ADMIT_UNKNOWN]: {
    glyph: '✓',
    word: 'Admit',
    note: 'Valid ticket — not in this device’s list. Sync when you can.',
  },
  [VERDICT.DUPLICATE]: {
    glyph: '!',
    word: 'Already in',
    note: 'This ticket was already checked in on this device.',
  },
  [VERDICT.REJECT]: { glyph: '✕', word: 'Reject', note: null },
  [VERDICT.WRONG_EVENT]: {
    glyph: '✕',
    word: 'Wrong event',
    note: 'This ticket is for a different event.',
  },
  [VERDICT.UNREADABLE]: {
    glyph: '?',
    word: 'Not a ticket',
    note: 'That code is not a KontriTicket ticket.',
  },
};

/** A full-bleed verdict overlay. `detail` carries the ticket number when known. */
export function verdictOverlay(verdict, detail, note) {
  const face = FACE[verdict] ?? FACE[VERDICT.UNREADABLE];
  return el('div', { class: `verdict v${verdict}` }, [
    el('div', { class: 'verdictGlyph' }, face.glyph),
    el('p', { class: 'verdictWord' }, face.word),
    detail ? el('div', { class: 'verdictDetail' }, detail) : '',
    note || face.note ? el('p', { class: 'verdictNote' }, note ?? face.note) : '',
  ]);
}

export function centre(children) {
  return el('div', { class: 'centre' }, children);
}
